import { makeAutoObservable } from 'mobx';

export interface FilterValues {
  applyFilter: boolean;
  assignedBy: string;
  assignedTo: string;
  fromDate: string | null;
  location: string;
  payRangeFrom: string;
  payRangeTo: string;
  payRangeChanged: boolean;
  salaryFrom: number;
  salaryTo: number;
  skillData: any[];
  tabSearch: string;
  title: string;
  toDate: string | null;
  workStatus: string;
  jobSourceId: string[];
}

const getDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getSevenDaysAgo = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return getDateString(date);
};

const getToday = (): string => {
  return getDateString(new Date());
};

export class ColumnFilterStore {
  filterValues: FilterValues = {
    applyFilter: false,
    assignedBy: '',
    assignedTo: '',
    fromDate: getSevenDaysAgo(),
    jobSourceId: [],
    location: '',
    payRangeChanged: false,
    payRangeFrom: '15',
    payRangeTo: '150',
    salaryFrom: 0,
    salaryTo: 0,
    skillData: [],
    tabSearch: '1',
    title: '',
    toDate: getToday(),
    workStatus: '',
  };

  appliedFilters: FilterValues = { ...this.filterValues };

  constructor() {
    makeAutoObservable(this);
  }

  setFilterValue(field: keyof FilterValues, value: any) {
    (this.filterValues as any)[field] = value;
  }

  setApplyFilter(value: boolean) {
    this.filterValues.applyFilter = value;
    if (value) {
      this.appliedFilters = { ...this.filterValues };
    }
  }

  apply() {
    this.filterValues.applyFilter = true;
    this.appliedFilters = { ...this.filterValues };
  }

  setTabSearch(value: string) {
    this.filterValues.tabSearch = value;
  }

  setFilterValues(values: Partial<FilterValues>) {
    // Mark pay range as changed if user is updating pay fields
    const PAY_FIELDS: (keyof FilterValues)[] = [
      'payRangeFrom',
      'payRangeTo',
      'salaryFrom',
      'salaryTo',
    ];
    const touchingPay = PAY_FIELDS.some((f) => f in values);
    this.filterValues = {
      ...this.filterValues,
      ...values,
      payRangeChanged: touchingPay ? true : this.filterValues.payRangeChanged,
    };
  }

  reset() {
    const defaultValues = {
      applyFilter: false,
      assignedBy: '',
      assignedTo: '',
      fromDate: getSevenDaysAgo(),
      jobSourceId: [],
      location: '',
      payRangeChanged: false,
      payRangeFrom: '15',
      payRangeTo: '150',
      salaryFrom: 0,
      salaryTo: 0,
      skillData: [],
      tabSearch: '1',
      title: '',
      toDate: getToday(),
      workStatus: '',
    };
    this.filterValues = { ...defaultValues };
    this.appliedFilters = { ...defaultValues };
  }
}

export const columnFilterStore = new ColumnFilterStore();
